import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { authenticateFirebase } from "~/services/firebaseAuthService";
import { verifyAuthCredential, authCredential } from "~/services/loginService";
import { useDispatch } from "react-redux";
import Utils from "~/helpers/Utils";

const CODE_EXPIRATION_TIME = 30000;
const POLLING_INTERVAL = 1000;

const WhatsAppAuthContext = createContext();

export const useWhatsAppAuthContext = () => {
	const context = useContext(WhatsAppAuthContext);
	if (!context) {
		throw new Error("useWhatsAppAuthContext must be used within a WhatsAppAuthProvider");
	}
	return context;
};

export const WhatsAppAuthProvider = ({ children }) => {
	const [authStatus, setAuthStatus] = useState(null);
	const [openModalCustom, setOpenModalCustom] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
	const [lastCode, setLastCode] = useState(null);
	const pollingIntervalRef = useRef(null);
	const timeoutRef = useRef(null);
	const isProcessingRef = useRef(false);
	const isStartingRef = useRef(false);
	const lastCodeRef = useRef(null);
	const lastOptionsRef = useRef(null);
	const history = useHistory();
	const dispatch = useDispatch();

	const stopPolling = useCallback(() => {
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
		}
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		isProcessingRef.current = false;
		isStartingRef.current = false;
		setIsLoading(false);
	}, []);

	const checkAuthStatus = useCallback(
		async (code, retryCount = 0) => {
			if (!code || isProcessingRef.current) {
				return null;
			}

			const MAX_RETRIES = 2;

			try {
				const response = await verifyAuthCredential(code);
				const status = response?.data?.status;
				const token = response?.data?.message;
				const currentPath = response?.data?.currentPath;
				let responseData = null;

				if (response?.data?.data) {
					responseData = JSON.parse(response.data.data);
				}
						
				const goBackRoute = responseData?.goBackRoute;

				if (status) {
					setAuthStatus(status);
					setError(null);
				}
				
				return { status, token, currentPath, goBackRoute, responseData };
			} catch (error) {
				if (error?.response?.status === 404) {
					return null;
				}

				const isNetworkError = error?.message === "Network Error" || error?.response?.status === 0;

				if (isNetworkError && retryCount < MAX_RETRIES) {
					const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
					await new Promise(resolve => setTimeout(resolve, delay));

					return checkAuthStatus(code, retryCount + 1);
				}

				if (isNetworkError && retryCount >= MAX_RETRIES) {
					setAuthStatus("CONNECTION_ERROR");
					setError("Problemas de conexão detectados. Por favor, verifique sua internet e tente novamente mais tarde.");

					setTimeout(() => {
						setShowModal(false);
						stopPolling();
						setAuthStatus(null);
					}, 2000);

					return "CONNECTION_ERROR"
				}


				setAuthStatus("ERROR");
				setError(error?.response?.data?.message || "An unexpected error occurred. Please try again.");
				stopPolling();
				return "ERROR";
			}
		},
		[stopPolling]
	);

	const startPolling = useCallback(
		(code, { redirectRoute, fallbackRoute, onSuccess, onError, goBackRouteProps, showSuccessModal = false } = {}) => {
			if (pollingIntervalRef.current || isStartingRef.current) {
				console.warn("Polling already in progress");
				return;
			}

			if (!code) {
				console.error("No code provided to startPolling");
				return;
			}
			stopPolling();
			isStartingRef.current = true; // trava síncrona antes do primeiro await

			isProcessingRef.current = false;
			setAuthStatus("PROCESS");
			setIsLoading(true);
			setError(null);
			
			lastOptionsRef.current = { redirectRoute, fallbackRoute, onSuccess, onError, goBackRouteProps, showSuccessModal };
			
			checkAuthStatus(code).then(async (result = {}) => {
				if (!result || typeof result === "string") {
					stopPolling();
					setIsLoading(false);
					if (onError) onError(new Error("Authentication error"));
					return;
				}
				const { status, token, responseData } = result;

				if (status === "OK") {
					setAuthStatus("FINALIZING");
					stopPolling();
					setIsLoading(false);
					setTimeout(async () => {
						await authenticateFirebase(token, {
							redirectRoute,
							dispatch,
							redirectOptions: {
								goBackRoute: redirectRoute,
								...goBackRouteProps,
							},
							onSuccess: (authResult) => {
								localStorage.setItem("@prit-web/TypeLogin", JSON.stringify("whatsapp"));
								localStorage.setItem("@prit-web/justAuthenticated", "true");
								localStorage.removeItem("@prit-web/lastCode");
								localStorage.removeItem("@prit-web/lastRedirectRoute");
								localStorage.removeItem("@prit-web/lastGoBackRouteProps");

								if (!authResult?.userData?.name) {
									setShowModal(false);
									setOpenModalCustom("REGISTER_NAME_USER");
									if (onSuccess) {
										onSuccess({ status, token, authResult, responseData });
									}
									return;
								}

								const shouldShowSuccess = lastOptionsRef.current?.showSuccessModal;

								if (shouldShowSuccess) {
									setAuthStatus("SUCCESS");
									setIsLoading(false);
									localStorage.removeItem("@prit-web/showLogin");
								} else {
									setShowModal(false);
									const finalRoute = authResult?.redirectRoute || redirectRoute;
									const redirectOptions = authResult?.redirectOptions || {};
									if (finalRoute) {
										history.push(finalRoute, redirectOptions);
									}
								}

								if (onSuccess) {
									onSuccess({ status, token, authResult, responseData });
								}
							},
							onError: (errorResult) => {
								setShowModal(false)
								if (onError) onError(new Error(errorResult?.error || "Erro ao autenticar"));
							},
							showOpenModalCustom: openModalCustom
						});
					}, 1000);
					return;
				}
				
				if (status === "EXPIRED_CODE") {
					setShowModal(false);
					stopPolling();
					setIsLoading(false);
					
					if (onSuccess) {
						onSuccess({ status, token: null, authResult: null, responseData });
					} 
					return;
				}
				
				if (status === "ERROR") {
					setShowModal(false);
					stopPolling();
					setIsLoading(false);
					if (onError) onError(new Error(error?.message || "Authentication error"));
					return;
				}
				
				if (pollingIntervalRef.current) {
						clearInterval(pollingIntervalRef.current);
						pollingIntervalRef.current = null;
					}
				isStartingRef.current = false;
				pollingIntervalRef.current = setInterval(async () => {
					const pollResult = await checkAuthStatus(code);
					if (!pollResult || typeof pollResult === "string") {
						stopPolling();
						setIsLoading(false);
						if (onError) onError(new Error("Authentication error"));
						return;
					}
					const {
						status: currentStatus,
						token: currentToken,
						responseData: responseDataFromPolling,
					} = pollResult;

					if (currentStatus === "OK") {
						setAuthStatus("FINALIZING");
						stopPolling();
						setIsLoading(false);
						setTimeout(async () => {
							await authenticateFirebase(currentToken, {
								redirectRoute,
								dispatch,
								redirectOptions: {
									goBackRoute: redirectRoute,
									...goBackRouteProps, 
								},
								onSuccess: (authResult) => {
									localStorage.setItem("@prit-web/TypeLogin", JSON.stringify("whatsapp"));
									localStorage.setItem("@prit-web/justAuthenticated", "true");
									localStorage.removeItem("@prit-web/lastCode");
									localStorage.removeItem("@prit-web/lastRedirectRoute");
									localStorage.removeItem("@prit-web/lastGoBackRouteProps");

									if (!authResult?.userData?.name) {
										setShowModal(false);
										setOpenModalCustom("REGISTER_NAME_USER");
										if (onSuccess) {
											onSuccess({ status: currentStatus, token: currentToken, authResult, responseData: responseDataFromPolling });
										}
										return;
									}

									const shouldShowSuccess = lastOptionsRef.current?.showSuccessModal;

									if (shouldShowSuccess) {
										setAuthStatus("SUCCESS");
										setIsLoading(false);
										localStorage.removeItem("@prit-web/showLogin");
									} else {
										setShowModal(false);
										const finalRoute = authResult?.redirectRoute || redirectRoute;
										const redirectOptions = authResult?.redirectOptions || {};
										if (finalRoute) {
											history.push(finalRoute, redirectOptions);
										}
									}

									if (onSuccess) {
										onSuccess({ status: currentStatus, token: currentToken, authResult, responseData: responseDataFromPolling });
									}
								},
								onError: (errorResult) => {
									setShowModal(false);
									if (onError) onError(new Error(errorResult?.error || "Erro ao autenticar"));
								},
								showOpenModalCustom: openModalCustom
							});
						}, 1000);
					} else if (currentStatus === "EXPIRED_CODE") {
						setShowModal(false);
						stopPolling();
						setIsLoading(false);
						
						if (onSuccess) {
							onSuccess({ status: currentStatus, token: null, authResult: null, responseData: responseDataFromPolling });
						} 
					} else if (currentStatus === "ERROR") {
							setShowModal(false);
							stopPolling();
							setIsLoading(false);
							if (onError) onError(new Error(error?.message || "Authentication error"));
						}
				}, POLLING_INTERVAL);
			});

			timeoutRef.current = setTimeout(() => {
				if (pollingIntervalRef.current || isStartingRef.current) {
					setShowModal(false);
					stopPolling();
					setAuthStatus(null);
					lastCodeRef.current = null;
					setError("Authentication timed out. Please try again.");
					setIsLoading(false);
					if (onError) {
						onError(new Error("Authentication timed out"));
					}
				}
			}, CODE_EXPIRATION_TIME);
		},
		[checkAuthStatus, stopPolling]
	);

	const openWhatsApp = (link, onError) => {
		try {
			
			const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
			if (isMobile) {
				window.location.href = link;
				return true;
			}
			const win = window.open(link, "_blank");
			if (!win) {
				const err = new Error("Não foi possível abrir o WhatsApp");
				onError && onError(err);
				return false;
			}
			return true;
		} catch (err) {
			onError && onError(err);
			return false;
		}
	};

	const requestAuthCredential = async ({
		language,
		onError,
		redirectRoute,
		fallbackRoute,
		goBackRouteProps,
		showSuccessModal = false,
		onSuccess,
		authContext,
	} = {}) => {
		try {
			// Cancela qualquer polling em curso (ex: checkStoredCode) e limpa o código
			// anterior do localStorage antes do await, evitando corrida com checkStoredCode.
			stopPolling();
			localStorage.removeItem("@prit-web/lastCode");
			localStorage.removeItem("@prit-web/lastRedirectRoute");
			localStorage.removeItem("@prit-web/lastGoBackRouteProps");
			setLastCode(null);
			lastCodeRef.current = null;

			const response = await authCredential(language || "pt-BR", authContext);
			const { linkWhatsapp, code } = response.data || {};
			if (!code || !linkWhatsapp) {
				throw new Error("Resposta inválida do servidor");
			}
		
		setTimeout(() => {
			setShowModal(true);
		}, 2000);
		setLastCode(code);
		lastCodeRef.current = code;
		localStorage.setItem("@prit-web/lastCode", JSON.stringify(code));

		if (redirectRoute) {
			localStorage.setItem("@prit-web/lastRedirectRoute", JSON.stringify(redirectRoute));
		  } else {
			localStorage.removeItem("@prit-web/lastRedirectRoute");
		  }

		if (goBackRouteProps) {
			localStorage.setItem("@prit-web/lastGoBackRouteProps", JSON.stringify(goBackRouteProps));
		} else {
			localStorage.removeItem("@prit-web/lastGoBackRouteProps");
		}

			const opened = openWhatsApp(linkWhatsapp, onError);
			if (!opened) {
				console.warn("Não foi possível abrir o WhatsApp em nova aba/janela.");
			}

				startPolling(code, {
					redirectRoute,
					fallbackRoute,
					goBackRouteProps,
					showSuccessModal,
					onSuccess: onSuccess || (() => {}),
					onError: () => {
						setShowModal(false);
					},
				});

			return { code, linkWhatsapp };
		} catch (err) {
			setShowModal(false);
			return null;
		}
	};

	const reset = useCallback(() => {
		stopPolling();
		setAuthStatus(null);
		setIsLoading(false);
		setError(null);
		isProcessingRef.current = false;
		lastCodeRef.current = null;
		setLastCode(null);
	}, [stopPolling]);

	const handleFinalizeAuth = useCallback(() => {
		const storedRedirectRoute = Utils.safeLocalGet("@prit-web/lastRedirectRoute");
		const storedGoBackRouteProps = JSON.parse(localStorage.getItem("@prit-web/lastGoBackRouteProps") || "{}");

		setShowModal(false);
		localStorage.removeItem("@prit-web/lastCode");
		setLastCode(null);
		localStorage.removeItem("@prit-web/lastRedirectRoute");
		localStorage.removeItem("@prit-web/lastGoBackRouteProps");
		
		const finalRoute = storedRedirectRoute || "/";
		if (finalRoute) {
			history.push(finalRoute, storedGoBackRouteProps);
		}
		
		reset();
	}, [history, reset, setLastCode]);

	React.useEffect(() => {
		return () => {
			stopPolling();
		};
	}, [stopPolling]);

	React.useEffect(() => {
		const unlisten = history.listen(() => {
			if (pollingIntervalRef.current) {
				stopPolling();
				setShowModal(false);
				setLastCode(null);
				lastCodeRef.current = null;
				localStorage.removeItem("@prit-web/lastCode");
				localStorage.removeItem("@prit-web/lastRedirectRoute");
				localStorage.removeItem("@prit-web/lastGoBackRouteProps");
			}
		});
		return () => unlisten();
	}, [history, stopPolling]);

	React.useEffect(() => {
		const checkStoredCode = async () => {
			try {
				const rawStoredCode = Utils.safeLocalGet("@prit-web/lastCode");
				// Se o JSON.parse converteu o code para número (formato antigo no localStorage),
				// o valor original está irrecuperável — descarta e força nova autenticação.
				const storedCode = rawStoredCode != null && typeof rawStoredCode !== "number"
					? String(rawStoredCode)
					: null;
				const storedRedirectRoute = Utils.safeLocalGet("@prit-web/lastRedirectRoute");
				const storedGoBackRouteProps = JSON.parse(localStorage.getItem("@prit-web/lastGoBackRouteProps") || "{}");

				if (storedCode && !pollingIntervalRef.current && !isProcessingRef.current) {
					lastCodeRef.current = storedCode;
					setLastCode(storedCode)
					
					const result = await checkAuthStatus(storedCode);
					
					if (result && result.status) {
						const { status, token } = result;
						
					if (status === "PROCESS") {
						setAuthStatus("PROCESS");
						setIsLoading(true);
						setShowModal(true);
						
						startPolling(storedCode, {
							redirectRoute: storedRedirectRoute,
							goBackRouteProps: storedGoBackRouteProps,
							onSuccess: () => {
								setShowModal(false);
								localStorage.removeItem("@prit-web/lastCode");
								setLastCode(null)
								localStorage.removeItem("@prit-web/lastRedirectRoute");
								localStorage.removeItem("@prit-web/lastGoBackRouteProps");
							},
							onError: () => {
								setShowModal(false);
								localStorage.removeItem("@prit-web/lastCode");
								setLastCode(null)
								localStorage.removeItem("@prit-web/lastRedirectRoute");
								localStorage.removeItem("@prit-web/lastGoBackRouteProps");
							},
						});
					} else if (status === "OK" && token) {
						setAuthStatus("FINALIZING");
						setIsLoading(true);
						setShowModal(true);
						setTimeout(async () => {
							await authenticateFirebase(token, {
								redirectRoute: storedRedirectRoute,
								dispatch,
								redirectOptions: {
									goBackRoute: storedRedirectRoute,
									...storedGoBackRouteProps,
								},
								onSuccess: (authResult) => {
									localStorage.setItem("@prit-web/TypeLogin", JSON.stringify("whatsapp"));
									localStorage.setItem("@prit-web/justAuthenticated", "true");
									const finalRoute = authResult?.redirectRoute || storedRedirectRoute;
									const redirectOptions = authResult?.redirectOptions || {};
									setIsLoading(false);
									setShowModal(false);
									if (finalRoute) {
										history.push(finalRoute, redirectOptions);
									}
									localStorage.removeItem("@prit-web/lastCode");
									setLastCode(null);
									localStorage.removeItem("@prit-web/lastRedirectRoute");
									localStorage.removeItem("@prit-web/lastGoBackRouteProps");
								},
								onError: (errorResult) => {
									setAuthStatus("ERROR");
									setError(errorResult?.error || "Erro ao autenticar");
									setIsLoading(false);
									setShowModal(false);
									localStorage.removeItem("@prit-web/lastCode");
									setLastCode(null)
									localStorage.removeItem("@prit-web/lastRedirectRoute");
									localStorage.removeItem("@prit-web/lastGoBackRouteProps");
								},
							});
						}, 1000);
					} else if (status === "EXPIRED_CODE") {
						stopPolling();
						setIsLoading(false);
						setShowModal(false);
						localStorage.removeItem("@prit-web/lastCode");
						setLastCode(null)
						localStorage.removeItem("@prit-web/lastRedirectRoute");
						localStorage.removeItem("@prit-web/lastGoBackRouteProps");
					}  else {
						localStorage.removeItem("@prit-web/lastCode");
						setLastCode(null)
						localStorage.removeItem("@prit-web/lastRedirectRoute");
						localStorage.removeItem("@prit-web/lastGoBackRouteProps");
					}

				} else {
					localStorage.removeItem("@prit-web/lastCode");
					setLastCode(null)
					localStorage.removeItem("@prit-web/lastRedirectRoute");
					localStorage.removeItem("@prit-web/lastGoBackRouteProps");
				}	
						
					
				}
			} catch (error) {
                setIsLoading(false);
			    setShowModal(false);
                setError(error?.response?.data?.message || "An unexpected error occurred. Please try again.");
				localStorage.removeItem("@prit-web/lastCode");
				setLastCode(null)
				localStorage.removeItem("@prit-web/lastRedirectRoute");
				localStorage.removeItem("@prit-web/lastGoBackRouteProps");
			} 
		};
	
		checkStoredCode();
	}, []);

	const value = {
		authStatus,
		setAuthStatus,
		isLoading,
		error,
		startPolling,
		stopPolling,
		reset,
		requestAuthCredential,
		lastCode: lastCode,
        showModal,
        setShowModal,
		handleFinalizeAuth,
		openModalCustom,
		setOpenModalCustom,
	};

	return <WhatsAppAuthContext.Provider value={value}>{children}</WhatsAppAuthContext.Provider>;
};
